import { Box, Text, useColorModeValue } from "@chakra-ui/react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import React, { useState, useEffect, useRef } from "react";
// @ts-ignore
import bg from '../assets/photo_bat/construction4.jpg';
// @ts-ignore
import smallBg from '../assets/photo_bat/construction4photo.jpg';
import { useTranslation } from 'react-i18next';

const MotionBox = motion(Box);
const MotionText = motion(Text);
const MotionSpan = motion.span;

/** Splits text into individual letter spans with staggered animation */
const AnimatedTitle: React.FC<{
    text: string;
    delay?: number;
    fontSize?: object | string;
    fontWeight?: string | number;
    color?: string;
    letterSpacing?: string;
}> = ({ text, delay = 0, fontSize, fontWeight = "200", color, letterSpacing = "-0.03em" }) => {
    const letters = text.split("");

    const container = {
        hidden: {},
        visible: {
            transition: { staggerChildren: 0.04, delayChildren: delay },
        },
    };

    const letterVariant = {
        hidden: { opacity: 0, y: 30, rotateX: -40 },
        visible: {
            opacity: 1,
            y: 0,
            rotateX: 0,
            transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
        },
    };

    return (
        <motion.span
            variants={container}
            initial="hidden"
            animate="visible"
            style={{
                display: "inline-block",
                perspective: "600px",
                fontSize: "inherit",
                fontWeight: "inherit",
                color: "inherit",
                letterSpacing,
                lineHeight: 1.05,
            }}
        >
            {letters.map((char, i) =>
                char === " " ? (
                    <MotionSpan key={i} variants={letterVariant} style={{ display: "inline-block" }}>
                        &nbsp;
                    </MotionSpan>
                ) : (
                    <MotionSpan
                        key={i}
                        variants={letterVariant}
                        style={{ display: "inline-block" }}
                    >
                        {char}
                    </MotionSpan>
                )
            )}
        </motion.span>
    );
};

const Zebi = () => {
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);
    const { scrollY } = useScroll();

    // Parallax: image zooms very gently on scroll
    const scale = useTransform(scrollY, [0, 600], [1, 1.12]);
    const overlayOpacity = useTransform(scrollY, [0, 400], [0, 0.4]);

    const overlayBg = useColorModeValue(
        "rgba(244,243,239,0.38)",
        "rgba(0,0,0,0.45)"
    );
    const textColor = useColorModeValue("#0A1628", "#F0EBE0");

    const titleStr = t('Titre');
    const subtitleStr = t('SousTitre');

    return (
        <Box
            ref={containerRef}
            position="fixed"
            top="0"
            left="0"
            width="100%"
            minHeight="100vh"
            zIndex="-1"
            overflow="hidden"
        >
            {/* Parallax background */}
            <MotionBox
                style={{ scale }}
                position="absolute"
                inset="-5%"
                bgImage={{ base: `url(${smallBg})`, md: `url(${bg})` }}
                bgSize="cover"
                bgPosition="center"
                filter="saturate(0.85) brightness(0.96)"
            />

            {/* Static overlay */}
            <Box
                position="absolute"
                inset={0}
                bg={overlayBg}
                backdropFilter="blur(0px)"
            />

            {/* Scroll-driven darkening */}
            <MotionBox
                position="absolute"
                inset={0}
                bg="rgba(0,0,0,1)"
                style={{ opacity: overlayOpacity }}
            />

            {/* Subtle grain texture */}
            <Box
                position="absolute"
                inset={0}
                opacity={0.04}
                bgImage="url(data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E)"
                bgSize="128px 128px"
                pointerEvents="none"
            />

            {/* Content */}
            <Box
                position="relative"
                minHeight="100vh"
                display="flex"
                flexDirection="column"
                alignItems="flex-end"
                justifyContent="center"
                px={{ base: 6, md: 16, lg: 24 }}
                pt={{ base: "25%", md: "15%" }}
            >

                {/* Main title — letter by letter */}
                <Box textAlign="right" mb={4} color={textColor}>
                    <Text
                        as="div"
                        fontSize={{ base: "4xl", md: "7xl", lg: "8xl" }}
                        fontWeight="200"
                        lineHeight="1.05"
                        color={textColor}
                    >
                        <AnimatedTitle text={titleStr} delay={0.4} />
                    </Text>
                </Box>

                {/* Divider line */}
                <motion.div
                    initial={{ scaleX: 0, originX: 1 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 1, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
                    style={{
                        width: "80px",
                        height: "1px",
                        backgroundColor: textColor,
                        opacity: 0.25,
                        marginBottom: "1.5rem",
                    }}
                />

                {/* Subtitle — letter by letter, lighter weight */}
                <Box textAlign="right" color={textColor}>
                    <Text
                        as="div"
                        fontSize={{ base: "lg", md: "3xl", lg: "4xl" }}
                        fontWeight="300"
                        letterSpacing="0.02em"
                        opacity={0.75}
                        color={textColor}
                    >
                        <AnimatedTitle text={subtitleStr} delay={0.9} letterSpacing="0.02em" />
                    </Text>
                </Box>

                {/* Scroll indicator */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2.2, duration: 1 }}
                    style={{
                        position: "absolute",
                        bottom: "2.5rem",
                        right: "1.5rem",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "8px",
                    }}
                >
                    <Text
                        fontSize="10px"
                        letterSpacing="0.25em"
                        textTransform="uppercase"
                        color={textColor}
                        opacity={0.4}
                    >
                        Scroll
                    </Text>
                    <motion.div
                        animate={{ y: [0, 8, 0] }}
                        transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
                        style={{
                            width: "1px",
                            height: "40px",
                            backgroundColor: textColor,
                            opacity: 0.3,
                        }}
                    />
                </motion.div>
            </Box>
        </Box>
    );
};

export default Zebi;